import { Download, Print } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { browserShowsPdfs, contractFileName } from '../utils/contractDocument';

const FRAME_STYLE: CSSProperties = { width: '100%', height: '75vh', border: 0, display: 'block' };

interface ContractPdfPreviewDialogProps {
    open: boolean;
    contractNumber: string;
    /** Object URL of the rendered PDF; null while it is still being rendered or when it failed. */
    url: string | null;
    /** Why the document could not be produced; shown instead of the document. */
    error: string | null;
    /** The browser already refused to print the document directly, so the viewer has to. */
    printBlocked?: boolean;
    onClose: () => void;
}

/**
 * Shows a saved contract's PDF on screen, with a button that prints it and one that saves it.
 *
 * A browser set to download PDFs instead of opening them (Chrome's "Download PDFs") shows nothing
 * in a frame and downloads the file again on every render, so there the document is not framed at
 * all: the dialog says why and offers the file, which then prints from whatever opens it.
 */
const ContractPdfPreviewDialog = ({
    open,
    contractNumber,
    url,
    error,
    printBlocked = false,
    onClose,
}: ContractPdfPreviewDialogProps) => {
    const { t } = useTranslation();
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [printRefused, setPrintRefused] = useState(false);
    const showsPdfs = browserShowsPdfs();

    // A new document starts without the previous one's refusal.
    useEffect(() => setPrintRefused(false), [url]);

    // The frame cannot be scripted when a PDF extension has taken the document over onto its
    // own origin; the viewer's own print button still works then.
    const printDocument = () => {
        try {
            const frameWindow = frameRef.current?.contentWindow;
            frameWindow?.focus();
            frameWindow?.print();
        } catch (printError) {
            console.warn('Printing the contract preview directly was blocked:', printError);
            setPrintRefused(true);
        }
    };

    const renderBody = () => {
        if (error !== null) {
            return (
                <Alert severity="error">
                    <Typography variant="body2">
                        {t('contracts.documentPreview.loadError')}
                    </Typography>
                    <Typography variant="body2">{error}</Typography>
                </Alert>
            );
        }
        if (url === null) {
            return (
                <Box display="flex" alignItems="center" justifyContent="center" gap={2} py={6}>
                    <CircularProgress size={28} />
                    <Typography>{t('contracts.documentPreview.loading')}</Typography>
                </Box>
            );
        }
        if (!showsPdfs) {
            return <Alert severity="info">{t('contracts.documentPreview.noPdfViewer')}</Alert>;
        }
        return (
            <>
                {(printBlocked || printRefused) && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t('contracts.documentPreview.printBlocked')}
                    </Alert>
                )}
                <iframe
                    ref={frameRef}
                    title={t('contracts.documentPreview.frameTitle')}
                    src={url}
                    style={FRAME_STYLE}
                />
            </>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            aria-labelledby="contract-pdf-preview-title"
        >
            <DialogTitle id="contract-pdf-preview-title">
                {t('contracts.documentPreview.title', { number: contractNumber })}
            </DialogTitle>
            <DialogContent dividers>{renderBody()}</DialogContent>
            <DialogActions>
                <Button
                    startIcon={<Download />}
                    component="a"
                    href={url ?? undefined}
                    download={contractFileName(contractNumber, 'pdf')}
                    disabled={url === null}
                >
                    {t('contracts.documentPreview.download')}
                </Button>
                <Button
                    startIcon={<Print />}
                    onClick={printDocument}
                    disabled={url === null || !showsPdfs}
                >
                    {t('contracts.documentPreview.print')}
                </Button>
                <Button onClick={onClose} variant="contained">
                    {t('common.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContractPdfPreviewDialog;
